// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CreateNewGroupModal from "@bg/App/Components/RfqRequestQuote/CreateNewGroupModal";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { ButtonPresentationProps } from "@bg/MobiusOverrides/Button/Button";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { parseBoolean } from "@bg/XDL/Utilities";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import translate from "@insite/client-framework/Translate";
import { ProductDto, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import OrderUpload, { OrderUploadStyles } from "@insite/content-library/Components/OrderUpload";
import OrderUploadErrorsModal, {
    OrderUploadErrorsModalStyles,
} from "@insite/content-library/Components/OrderUploadErrorsModal";
import ProductSelector, { ProductSelectorStyles } from "@insite/content-library/Components/ProductSelector";
import ProductSelectorVariantModal from "@insite/content-library/Components/ProductSelectorVariantModal";
import Button from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quoteLength?: number;
    quote?: QuoteModel;
    widgetId: string;
    isSalesPersonView?: boolean;
    quoteAll?: () => void;
}

const mapStateToProps = (state: CustomAppState) => ({
    uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
    isQuoteFreight: state.commonActions["isQuoteFreight"],
    freightValue: state.commonActions["freightValue"],
    isQuoteDisabled: state.commonActions.isQuoteDisabled,
});

const mapDispatchToProps = {
    addCartLines: makeHandlerChainAwaitable(addCartLines as any),
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface RequestQuoteOptionsStyle {
    container?: InjectableCss;
    countsWrapper?: InjectableCss;
    count?: TypographyPresentationProps;
    addItems?: LinkPresentationProps;
    createGroupWrapper?: InjectableCss;
    createGroup?: LinkPresentationProps;
    addItemsGridContainer?: InjectableCss;
    productSelectorGridItem?: InjectableCss;
    productSelectorStyles?: ProductSelectorStyles;
    uploadItemsGridItem?: InjectableCss;
    uploadItemsLink?: LinkPresentationProps;
    uploadItemsModal?: ModalPresentationProps;
    orderUploadStyles?: OrderUploadStyles;
    orderUploadErrorsModalStyles?: OrderUploadErrorsModalStyles;
    closeAddItemsToList?: IconProps;
    quoteAllButton?: ButtonPresentationProps;
    freightWrapper?: InjectableCss;
    freightValue?: TextFieldPresentationProps;
    actionsPopup?: InjectableCss;
}

export const styles: RequestQuoteOptionsStyle = {
    container: {
        css: css`
            padding: 0 0 5px;
            &&.customer-view {
                padding: 0 0 15px;
            }
            @media (max-width: 768px) {
                padding-bottom: 15px;
            }
        `,
    },
    countsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            position: relative;
            @media (max-width: 1000px) {
                flex-wrap: wrap;
            }
        `,
    },
    count: {
        variant: "h6",
        css: css`
            display: block;
            margin-right: 30px;
        `,
    },
    addItems: linkIconStyleGenerator(
        "AddToList",
        "15px",
        12,
        `font-weight: ${fontConfig.weight.semiBold};`,
        "",
        "margin-right: 30px;",
    ),
    createGroupWrapper: {
        css: css`
            flex-grow: 1;
            display: flex;
            justify-content: flex-end;
            @media (max-width: 481px) {
                justify-content: flex-start;
                width: 100%;
            }
        `,
    },
    createGroup: linkIconStyleGenerator(
        "CreateGroup",
        "15px",
        14,
        `opacity: 0.8; font-weight: ${fontConfig.weight.semiBold};`,
    ),
    productSelectorGridItem: {
        css: css`
            display: flex;
            @media (max-width: 680px) {
                flex-wrap: wrap;
            }
        `,
    },
    productSelectorStyles: {
        container: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                padding: 0;
                width: 100%;
                align-items: flex-start;
            `,
        },
        searchWrapper: {
            css: css`
                max-width: 450px;
                width: 100%;
                margin-right: 30px;
            `,
        },
        searchDynamicDropdown: {
            cssOverrides: {
                formField: css`
                    flex-direction: row;
                    align-items: center;
                    width: 100%;

                    label {
                        width: auto;
                        font-weight: ${fontConfig.weight.bold};
                        font-size: ${fontConfig.fontSize.small}px;
                        margin-right: 8px;
                        white-space: nowrap;
                    }
                    @media (max-width: 650px) {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                `,
                formInputWrapper: css`
                    margin-top: 0 !important;
                `,
                inputSelect: css`
                    font-size: ${fontConfig.fontSize.small}px;
                    height: 32px;
                `,
                selectedText: css`
                    padding: 5px 10px;
                `,
                list: css`
                    top: 32px;
                    width: 100%;
                    border-top: 0;
                `,
            },
            iconProps: {
                css: css`
                    &&&&&& {
                        right: 0;
                        top: 0;
                    }
                `,
            },
        },
        qtyWrapper: {
            css: css`
                display: flex;
                align-items: center;
                width: 160px;
                padding: 0;
                margin-right: 30px;

                &&& > label {
                    width: auto;
                    font-weight: ${fontConfig.weight.bold};
                    font-size: ${fontConfig.fontSize.small}px;
                    margin-right: 8px;
                    white-space: nowrap;
                }

                label + div {
                    margin-top: 0;
                    height: 32px;
                }

                &&& input {
                    height: 32px !important;
                }
            `,
        },
        buttonWrapper: {
            css: css`
                @media (max-width: 481px) {
                    margin-top: 10px !important;
                }
            `,
        },
        selectButton: {
            css: css`
                min-height: 32px !important;
                margin-top: 0 !important;
            `,
        },
    } as ProductSelectorStyles,
    uploadItemsLink: {
        css: css`
            margin: 0;
            white-space: nowrap;
            margin-right: 15px;
            height: 32px;
        `,
    },
    closeAddItemsToList: {
        src: "X",
        css: css`
            margin: 0;
            margin-top: 5px;
            cursor: pointer;
            align-items: flex-start;
        `,
    },
    quoteAllButton: {
        css: css`
            &&&& {
                background: ${getColor("primary")};
                color: ${getColor("common.white")};
                margin-left: 30px;
                &&:hover {
                    background: ${getColor("common.white")};
                    color: ${getColor("primary")};
                    border-color: ${getColor("primary")};
                }
            }
        `,
    },
    freightWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    freightValue: {
        cssOverrides: {
            formField: css`
                max-width: 75px;
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
        },
    },
    actionsPopup: {
        css: css`
            position: absolute;
            right: 0;
            top: -10px;
        `,
    },
};

const RfqQuoteDetailsOptions = ({
    quoteLength,
    quote,
    widgetId,
    addCartLines,
    uncategorizedGroupName,
    isSalesPersonView,
    quoteAll,
    freightValue,
    isQuoteFreight,
    isQuoteDisabled,
}: Props) => {
    if (!quote) {
        return null;
    }
    const dispatch = useDispatch();
    const toasterContext = useContext(ToasterContext);
    const [addItemsToListIsOpen, setAddItemsToListIsOpen] = useState(false);
    const [uploadItemsModalIsOpen, setUploadItemsModalIsOpen] = useState(false);
    const [groupingModalIsOpen, setGroupingModalIsOpen] = useState(false);
    const [productInfo, setProductInfo] = useState<ProductInfo>({} as ProductInfo);
    const [creatingNewGroup, setIsCreatingNewGroup] = useState(false);
    const [errorMsg, setError] = useState("");

    useEffect(() => {
        if (isQuoteFreight?.quoteId !== quote.id) {
            dispatch({
                type: "commonActionSetter/SetAction",
                payload: {
                    name: "isQuoteFreight",
                    value: { isFrieght: false, quoteId: "" },
                },
            });
            dispatch({
                type: "commonActionSetter/SetAction",
                payload: {
                    name: "freightValue",
                    value: "",
                },
            });
        }
    }, []);

    const selectProductHandler = (productInfo: ProductInfo) => {
        const isAlreadyRelatedProduct = quote.quoteLineCollection?.filter(
            i => i.productId === productInfo.productId && i.properties.isRelatedProduct === "true",
        );
        if (isAlreadyRelatedProduct && isAlreadyRelatedProduct?.length > 0) {
            setError("Item is already added as a related product");
            return;
        }
        setError("");
        setGroupingModalIsOpen(true);
        setProductInfo(productInfo);
    };

    const uploadItemsClickHandler = () => {
        setUploadItemsModalIsOpen(true);
    };

    const uploadItemsModalCloseHandler = () => {
        setUploadItemsModalIsOpen(false);
    };

    const uploadProductsHandler = async (products: ProductDto[]) => {
        const productInfos = products.map(o => ({
            productId: o.id,
            qtyOrdered: o.qtyOrdered,
            unitOfMeasure: o.selectedUnitOfMeasure ?? "",
        }));
        await addCartLines({
            cartId: quote.id,
            productInfos,
            newGroup: true,
            groupName: uncategorizedGroupName,
            reloadQuote: true,
            onComplete(res: any) {
                if (res.cartLines?.length) {
                    toasterContext.addToast({ body: translate("Items Added"), messageType: "success" });
                }
            },
        });
        setUploadItemsModalIsOpen(false);
    };

    const openCreateGroupModal = () => {
        setGroupingModalIsOpen(true);
        setIsCreatingNewGroup(true);
    };

    const closeModalHandler = () => {
        setGroupingModalIsOpen(false);
        setIsCreatingNewGroup(false);
    };

    const callAfterSuccess = (selectedGroup: string) => {
        dispatch({
            type: "Pages/xProductListDetails/TemporarilySaveEmptyList",
            emptyList: selectedGroup,
        });
    };

    const setIsQuoteFreight: CheckboxProps["onChange"] = (_, value) => {
        dispatch({
            type: "commonActionSetter/SetAction",
            payload: {
                name: "isQuoteFreight",
                value: {
                    isFrieght: value,
                    quoteId: quote.id,
                },
            },
        });
    };

    const handleFreightChange = (e: any) => {
        // not allowing negative values
        let chargesValue = e.target.value;
        if (chargesValue < 0) {
            chargesValue = chargesValue * -1;
        }
        chargesValue = parseFloat(chargesValue.toString().replace("-", ""));
        dispatch({
            type: "commonActionSetter/SetAction",
            payload: {
                name: "freightValue",
                value: chargesValue,
            },
        });
    };

    if (parseBoolean(quote.properties.isExpired)) {
        return null;
    }

    return (
        <StyledWrapper
            {...styles.container}
            className={!isSalesPersonView || addItemsToListIsOpen ? "customer-view" : ""}
        >
            {!addItemsToListIsOpen && (
                <StyledWrapper {...styles.countsWrapper}>
                    <Typography {...styles.count}>
                        {quoteLength} {translate("Products")}
                    </Typography>
                    {isSalesPersonView && quote.isEditable && isQuoteDisabled && !isQuoteDisabled.isDisabled && (
                        <Hidden below="md">
                            <Link onClick={() => setAddItemsToListIsOpen(true)} {...styles.addItems}>
                                {translate("Add Items To Quote")}
                            </Link>
                        </Hidden>
                    )}
                    {isSalesPersonView && quote.isEditable && isQuoteDisabled && !isQuoteDisabled.isDisabled && (
                        <StyledWrapper
                            css={css`
                                display: flex;
                                align-items: center;
                                @media (max-width: 481px) {
                                    width: 100%;
                                    margin-top: 15px;
                                }
                            `}
                        >
                            <StyledWrapper
                                css={css`
                                    margin-right: 15px;
                                `}
                            >
                                <Checkbox
                                    disabled={quote.fulfillmentMethod.toLowerCase() === "pickup"}
                                    onChange={setIsQuoteFreight}
                                    checked={isQuoteFreight?.isFrieght}
                                >
                                    {translate("Freight Charges")}
                                </Checkbox>
                            </StyledWrapper>
                            {isQuoteFreight?.isFrieght && isQuoteDisabled && !isQuoteDisabled.isDisabled && (
                                <StyledWrapper {...styles.freightWrapper}>
                                    <TextField
                                        {...styles.freightValue}
                                        type="number"
                                        onChange={handleFreightChange}
                                        value={freightValue}
                                    />
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    )}
                    <Hidden below="md" {...styles.createGroupWrapper}>
                        {isQuoteDisabled && !isQuoteDisabled.isDisabled && (
                            <StyledWrapper {...styles.createGroupWrapper}>
                                <Link onClick={openCreateGroupModal} {...styles.createGroup}>
                                    {translate("Create a New Group")}
                                </Link>
                            </StyledWrapper>
                        )}
                        {isSalesPersonView && quote.isEditable && isQuoteDisabled && !isQuoteDisabled.isDisabled && (
                            <Button onClick={() => quoteAll!()} {...styles.quoteAllButton}>
                                {translate("Quote All")}
                            </Button>
                        )}
                    </Hidden>
                    <Hidden above="sm" {...styles.actionsPopup}>
                        <OverflowMenu>
                            {isSalesPersonView &&
                                quote.isEditable &&
                                isQuoteDisabled &&
                                !isQuoteDisabled.isDisabled && (
                                    <Clickable onClick={() => setAddItemsToListIsOpen(true)}>
                                        {translate("Add Items To Quote")}
                                    </Clickable>
                                )}
                            {isQuoteDisabled && !isQuoteDisabled.isDisabled && (
                                <Clickable onClick={openCreateGroupModal}>{translate("Create a New Group")}</Clickable>
                            )}
                            {isSalesPersonView &&
                                quote.isEditable &&
                                isQuoteDisabled &&
                                !isQuoteDisabled.isDisabled && (
                                    <Clickable onClick={() => quoteAll!()}>{translate("Quote All")}</Clickable>
                                )}
                        </OverflowMenu>
                    </Hidden>
                </StyledWrapper>
            )}
            {addItemsToListIsOpen && (
                <StyledWrapper {...styles.addItemsGridContainer}>
                    <StyledWrapper {...styles.productSelectorGridItem}>
                        <ProductSelector
                            selectButtonTitle={translate("Add to Quote")}
                            skipInventoryValidation={true}
                            onSelectProduct={selectProductHandler}
                            productIsConfigurableMessage={siteMessage("ListUpload_CannotOrderConfigurable")}
                            productIsUnavailableMessage={siteMessage("Product_NotFound")}
                            customErrorMessage={errorMsg}
                            extendedStyles={styles.productSelectorStyles}
                        />
                        <ProductSelectorVariantModal skipInventoryValidation={true} />
                        <Link {...styles.uploadItemsLink} onClick={uploadItemsClickHandler}>
                            {translate("Upload Items")}
                        </Link>
                        <Icon
                            {...styles.closeAddItemsToList}
                            onClick={() => {
                                setAddItemsToListIsOpen(false);
                            }}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.uploadItemsGridItem}>
                        <Modal
                            {...styles.uploadItemsModal}
                            headline={translate("Upload Items To Quote")}
                            isOpen={uploadItemsModalIsOpen}
                            handleClose={uploadItemsModalCloseHandler}
                        >
                            <StyledWrapper>
                                <StyledWrapper>
                                    <OrderUpload
                                        descriptionText={siteMessage("Lists_UploadLinkTooltip")}
                                        checkInventory={false}
                                        templateUrl="/Excel/ListUploadTemplate.xlsx"
                                        onUploadProducts={uploadProductsHandler}
                                        extendedStyles={styles.orderUploadStyles}
                                    />
                                    <OrderUploadErrorsModal
                                        descriptionText={siteMessage("ListUpload_UploadError")}
                                        uploadErrorText={siteMessage("ListUpload_UploadError")}
                                        rowsLimitExceededText={siteMessage("ListUpload_RowsLimitExceeded")}
                                        errorReasons={{
                                            [UploadError.ConfigurableProduct]: siteMessage(
                                                "ListUpload_CannotOrderConfigurable",
                                            ),
                                            [UploadError.StyledProduct]: siteMessage("ListUpload_CannotOrderStyled"),
                                            [UploadError.InvalidUnit]: translate("Invalid U/M"),
                                            [UploadError.NotFound]: siteMessage("Product_NotFound"),
                                        }}
                                        extendedStyles={styles.orderUploadErrorsModalStyles}
                                    />
                                </StyledWrapper>
                                <StyledWrapper>
                                    <Zone zoneName="UploadInstructions" contentId={widgetId} />
                                </StyledWrapper>
                            </StyledWrapper>
                        </Modal>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <CreateNewGroupModal
                productInfo={productInfo}
                creatingNewGroup={creatingNewGroup}
                groupingModalIsOpen={groupingModalIsOpen}
                closeModalCallback={closeModalHandler}
                callAfterSuccess={callAfterSuccess}
                cart={quote}
                isQuote={true}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsOptions);
